import { X } from 'lucide-react'
import { useStore } from '../store/useStore'

/**
 * EditorSettings - Modal to configure editor appearance and behavior
 */
export default function EditorSettings({ isOpen, onClose }) {
  const { editorSettings, updateEditorSettings } = useStore()
  
  if (!isOpen) return null
  
  const Toggle = ({ label, description, checked, onChange }) => (
    <label className="flex items-start gap-3 cursor-pointer group">
      <div className="relative mt-0.5">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <div className="w-9 h-5 bg-gray-600 rounded-full peer-checked:bg-p5-pink transition-colors" />
        <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-4" />
      </div>
      <div className="flex-1">
        <div className="text-sm text-white group-hover:text-p5-pink transition-colors">{label}</div>
        <div className="text-xs text-gray-400">{description}</div>
      </div>
    </label>
  )
  
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg w-full max-w-md border border-gray-700 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Editor Settings</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-700 rounded transition-colors"
          >
            <X size={18} className="text-gray-400" />
          </button>
        </div>
        
        {/* Settings */}
        <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Font Size */}
          <div>
            <label className="block text-sm text-white mb-2">Font Size</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="10"
                max="24"
                value={editorSettings.fontSize}
                onChange={(e) => updateEditorSettings({ fontSize: parseInt(e.target.value) })}
                className="flex-1 accent-p5-pink"
              />
              <span className="text-sm text-gray-400 w-8">{editorSettings.fontSize}px</span>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-4 space-y-4">
            <Toggle
              label="Word Wrap"
              description="Wrap long lines instead of horizontal scrolling"
              checked={editorSettings.wordWrap}
              onChange={(v) => updateEditorSettings({ wordWrap: v })}
            />
            
            <Toggle
              label="Minimap"
              description="Show code overview on the right side"
              checked={editorSettings.minimap}
              onChange={(v) => updateEditorSettings({ minimap: v })}
            />
            
            <Toggle
              label="Bracket Colors"
              description="Colorize matching brackets (rainbow style)"
              checked={editorSettings.bracketColors}
              onChange={(v) => updateEditorSettings({ bracketColors: v })}
            />
            
            <Toggle
              label="Indent Guides"
              description="Show vertical lines for indentation levels"
              checked={editorSettings.indentGuides}
              onChange={(v) => updateEditorSettings({ indentGuides: v })}
            />
            
            <Toggle
              label="Current Line Highlight"
              description="Highlight the line where cursor is"
              checked={editorSettings.lineHighlight}
              onChange={(v) => updateEditorSettings({ lineHighlight: v })}
            />
            
            <Toggle
              label="Smooth Scrolling"
              description="Animate scrolling and cursor movement"
              checked={editorSettings.smoothScrolling}
              onChange={(v) => updateEditorSettings({ smoothScrolling: v })}
            />
            
            <Toggle
              label="Ctrl+Scroll Zoom"
              description="Zoom in/out with Ctrl + mouse wheel"
              checked={editorSettings.mouseWheelZoom}
              onChange={(v) => updateEditorSettings({ mouseWheelZoom: v })}
            />
            
            <Toggle
              label="Sticky Scroll"
              description="Keep function headers visible while scrolling"
              checked={editorSettings.stickyScroll}
              onChange={(v) => updateEditorSettings({ stickyScroll: v })}
            />
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-p5-pink hover:bg-pink-600 rounded text-sm font-medium transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
